import React from 'react'
import PropTypes from 'prop-types'

import UnitComponent from 'components/UnitComponent'
import BackLink from 'components/BackLink'
import CoursePreviewBanner from 'components/CoursePreviewBanner'
import LearnErrorBoundary from 'components/LearnErrorBoundary'
import CourseComponent from 'components/CourseComponent'
import SiteSelector from 'components/SiteSelector'
import TieredInterventionUnitCard from 'components/TieredInterventionUnitCard'
import UnitOverview from 'experienceComponents/UnitOverview'
import { HeaderTwo } from 'secondstep-components'

import {
  BannerSpacer,
  UnitV3BodyBackground,
  UnitV3SectionWrapper,
  UnitV3SectionHeader,
  UnitV3TrainingCard,
  UnitV3TrainingCardLink,
  UnitV3TrainingDescription,
  UnitV3TrainingDot,
  UnitV3TrainingText,
  UnitV3TrainingTitle,
  PreviewBannerWrapper,
  UnitV3ContentWrapper,
  UnitV3LessonPageWrapper,
  UnitV3OverviewBox,
  UnitV3SiteSelectorWrapper,
  UnitV3TopNavigation,
  UnitV3PageWrapper,
} from './component.styles'
import {
  getCourseHeaderData,
  getPresentationMode,
  getUnitTabs,
  isLessonPageDisplay,
  prepareItemsForSummaryList,
} from './data'
import { getPathForRoute, getTrainingPathForRoute } from './helper'
import { BACK_LINK_DIGITAL_WELL_BEING } from './constants'
import { K5_PROGRAM_TYPE } from 'utils/productMaps'

/**
 * UnitV3 renders a unit fetched via curriculum-service v3.
 *
 * When the new `pageDisplayType` toggle is true ("lessonPage"), the page
 * renders link summaries as flattened lesson cards. Otherwise, it falls back
 * to the existing lesson accordion UI via `UnitComponent`.
 */
const UnitV3PageComponent = props => {
  const {
    unit,
    linkSummaries,
    trainingLinkSummary,
    match,
    location,
    productName,
    navigationEvent,
    onBacklinkClick,
    onSelectUnit,
    isPreview,
    classTitle,
    classInstanceId,
  } = props || {}

  // DWB unit URLs end with `:grade` (the grade segment resolves to the unit
  // entry), while legacy K8 unit URLs end with `:unit`. Try both so this
  // page can be reused across either route shape.
  const unitId = match?.params?.unit || match?.params?.grade

  if (!unit) {
    return null
  }

  const queryParamsString = location?.search || ''
  const presentationMode = getPresentationMode(match)
  const hasAccordionTabs = getUnitTabs(unit).length > 0
  const useLessonPageLayout = isLessonPageDisplay(unit) || !hasAccordionTabs
  const summaryItems = prepareItemsForSummaryList(linkSummaries)
  const lessonsHeaderText =
    summaryItems.length > 0 ? `Lessons 1-${summaryItems.length}` : 'Lessons'
  const { lessonsAmounts, scopeAndSequenceLinks } = getCourseHeaderData({
    unit,
    linkSummaries,
    productName,
  })

  const renderSummaryItem = ({ item, lessonNumber }) => {
    return (
      <TieredInterventionUnitCard
        description={item?.description}
        isPreviewMode={item?.isPreview ?? isPreview}
        lessonNumber={lessonNumber}
        markedComplete={item?.markedComplete}
        path={getPathForRoute({
          matchUrl: match?.url,
          queryParamsString,
          routeValue: item?.path,
        })}
        programType={K5_PROGRAM_TYPE}
        tags={item?.tags}
        title={item?.title}
      />
    )
  }

  const trainingCallout = trainingLinkSummary
    ? {
        title: trainingLinkSummary?.displayTitle,
        description: trainingLinkSummary?.description,
        route: trainingLinkSummary?.route,
      }
    : null

  const courseHeaderTitle = isPreview
    ? unit?.displayTitle
    : classTitle || unit?.displayTitle

  return (
    <LearnErrorBoundary>
      <UnitV3PageWrapper>
        {isPreview && (
          <PreviewBannerWrapper>
            <BannerSpacer />
            <CoursePreviewBanner />
          </PreviewBannerWrapper>
        )}
        <UnitV3ContentWrapper>
          <UnitV3TopNavigation className="breadcrumbs">
            <BackLink dataTestId="breadcrumb" onClick={onBacklinkClick}>
              {BACK_LINK_DIGITAL_WELL_BEING}
            </BackLink>
            <UnitV3SiteSelectorWrapper>
              <SiteSelector />
            </UnitV3SiteSelectorWrapper>
          </UnitV3TopNavigation>

          <CourseComponent
            displayTitle={unit?.displayTitle}
            instance={isPreview ? null : classInstanceId || presentationMode}
            lessonsAmounts={lessonsAmounts}
            scopeAndSequenceLinks={scopeAndSequenceLinks}
            title={courseHeaderTitle}
          >
            {useLessonPageLayout ? (
              <>
                <UnitV3OverviewBox id="unit-overviewbox" role="tabpanel">
                  <UnitOverview
                    contentfulItem={unit}
                    dataTestId={`unit-overview-${unitId}`}
                    tabIndex={0}
                  />
                </UnitV3OverviewBox>
                <UnitV3BodyBackground>
                  {trainingCallout?.route && (
                    <UnitV3SectionWrapper>
                      <UnitV3SectionHeader data-testid="unit-v3-training-heading">
                        <HeaderTwo>Teaching the Unit</HeaderTwo>
                      </UnitV3SectionHeader>
                      <UnitV3TrainingCardLink
                        data-testid="unit-v3-training-card"
                        to={getTrainingPathForRoute({
                          matchUrl: match?.url,
                          productName,
                          queryParamsString,
                          routeValue: trainingCallout.route,
                        })}
                      >
                        <UnitV3TrainingCard>
                          <UnitV3TrainingDot />
                          <UnitV3TrainingText>
                            <UnitV3TrainingTitle>
                              {trainingCallout.title}
                            </UnitV3TrainingTitle>
                            <UnitV3TrainingDescription>
                              {trainingCallout.description}
                            </UnitV3TrainingDescription>
                          </UnitV3TrainingText>
                        </UnitV3TrainingCard>
                      </UnitV3TrainingCardLink>
                    </UnitV3SectionWrapper>
                  )}
                  <UnitV3LessonPageWrapper>
                    <UnitV3SectionHeader data-testid="unit-v3-lessons-heading">
                      <HeaderTwo>{lessonsHeaderText}</HeaderTwo>
                    </UnitV3SectionHeader>
                    {summaryItems.map((item, index) => (
                      <React.Fragment
                        key={`${item?.path ||
                          item?.title ||
                          'lesson-item'}-${index}`}
                      >
                        {renderSummaryItem({ item, lessonNumber: index + 1 })}
                      </React.Fragment>
                    ))}
                  </UnitV3LessonPageWrapper>
                </UnitV3BodyBackground>
              </>
            ) : (
              <UnitComponent
                entryId={unit?.sys?.id}
                match={match}
                navigationEvent={navigationEvent}
                onSelectUnit={onSelectUnit}
                productName={productName}
                unit={unit}
                unitId={unitId}
              />
            )}
          </CourseComponent>
        </UnitV3ContentWrapper>
      </UnitV3PageWrapper>
    </LearnErrorBoundary>
  )
}

UnitV3PageComponent.propTypes = {
  classInstanceId: PropTypes.string,
  classTitle: PropTypes.string,
  isPreview: PropTypes.bool,
  linkSummaries: PropTypes.array,
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
  match: PropTypes.shape({
    url: PropTypes.string,
    params: PropTypes.shape({
      verb: PropTypes.string,
      course: PropTypes.string,
      unit: PropTypes.string,
      grade: PropTypes.string,
    }),
  }),
  navigationEvent: PropTypes.func,
  onBacklinkClick: PropTypes.func,
  onSelectUnit: PropTypes.func,
  productName: PropTypes.string,
  trainingLinkSummary: PropTypes.object,
  unit: PropTypes.shape({
    displayTitle: PropTypes.string,
    description: PropTypes.string,
    pageDisplayType: PropTypes.bool,
    lessons: PropTypes.array,
    sys: PropTypes.object,
  }),
}

UnitV3PageComponent.displayName = 'UnitV3PageComponent'

export default UnitV3PageComponent
